/**
 * ============================================
 * USE-THEME HOOK - Manejo de Tema Claro/Oscuro
 * ============================================
 *
 * Este hook maneja el estado del tema (light/dark) y lo persiste
 * en localStorage para que se mantenga entre sesiones.
 *
 * CÓMO FUNCIONA:
 * 1. Al cargar, lee el tema guardado en localStorage
 * 2. Si no hay tema guardado, usa la preferencia del sistema
 * 3. Agrega/quita la clase "dark" del elemento <html>
 * 4. Los estilos de Tailwind con "dark:" se activan automáticamente
 */

import { useEffect, useState, useMemo } from "react";

type Theme = "light" | "dark" | "system";

/**
 * Hook personalizado para manejar el tema
 *
 * @returns {Object} - { theme, setTheme, resolvedTheme }
 * - theme: El tema seleccionado ("light", "dark", "system")
 * - setTheme: Función para cambiar el tema
 * - resolvedTheme: El tema real aplicado ("light" o "dark")
 */
export function useTheme() {
  // Estado del tema seleccionado por el usuario
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("theme") as Theme;
      if (stored) return stored;
    }
    return "system";
  });

  // Estado para forzar re-render cuando cambia preferencia del sistema
  const [systemPreference, setSystemPreference] = useState<"light" | "dark">(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light";
  });

  // Calcular el tema resuelto usando useMemo (sin setState en render)
  const resolvedTheme = useMemo(() => {
    if (theme === "system") {
      return systemPreference;
    }
    return theme;
  }, [theme, systemPreference]);

  /**
   * Función para cambiar el tema
   * Guarda en localStorage y actualiza el estado
   */
  const setTheme = (newTheme: Theme) => {
    localStorage.setItem("theme", newTheme);
    setThemeState(newTheme);
  };

  /**
   * Efecto que aplica el tema al documento
   */
  useEffect(() => {
    const root = window.document.documentElement;

    // Aplicar o quitar la clase "dark" del <html>
    if (resolvedTheme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [resolvedTheme]);

  /**
   * Efecto para escuchar cambios en la preferencia del sistema
   */
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return { theme, setTheme, resolvedTheme };
}
